<template>
  <div class="duplicate-patient-container duplicate-patient-page-modal px-2 pt-0 pb-1">
    <!-- Modal Header Title -->
    <div class="modal-title-text text-h6 font-weight-bold grey--text text--darken-3 mb-4">
      Duplicate Patient Found
    </div>

    <!-- Top Info Banner -->
    <div class="info-banner d-flex align-center pa-3 mb-4 rounded-lg">
      <div class="info-left-accent"></div>
      <v-icon color="#3caada" class="mr-3 ml-2">{{ icons.mdiInformation }}</v-icon>
      <span class="info-text text-body-2">
        <template v-if="duplicatePatientsList.length === 1">
          A patient named <strong>{{ enteredPatientName }}</strong> already exists in our database.
        </template>
        <template v-else>
          Multiple patients named <strong>{{ enteredPatientName }}</strong> already exist in our database.
        </template>
      </span>
    </div>

    <!-- Instructions List -->
    <ul class="instructions-list text-body-2 grey--text text--darken-2 mb-6 pl-5">
      <li v-if="duplicatePatientsList.length > 1" class="mb-1">
        To use an existing profile, select the correct patient profile from the dropdown menu and click <strong>"Use Already Existing Patient."</strong>
      </li>
      <li v-else class="mb-1">
        To use the existing patient profile, click <strong>"Use Already Existing Patient."</strong>
      </li>
      <li>
        Otherwise, you can choose to create an entirely new patient record by clicking <strong>"Add as New Patient."</strong>
      </li>
    </ul>

    <!-- Grid Columns (col-sm-6 ensures 2 columns on iPad Mini / 768px and up) -->
    <div class="duplicate-patient-grid row no-gutters">
      <!-- Left Column: Existing Patient -->
      <div class="col-12 col-sm-6 pr-sm-3 pl-0 py-0 d-flex flex-column">
        <div class="column-header d-flex align-center pb-2 mb-3 border-bottom">
          <span class="count-badge mr-2">{{ duplicatePatientsList.length }}</span>
          <span class="font-weight-bold grey--text text--darken-3">
            Already Existing {{ duplicatePatientsList.length === 1 ? 'Patient' : 'Patients' }}
          </span>
        </div>

        <div v-if="duplicatePatientsList.length > 1" class="select-duplicate mb-4">
          <label class="dropdown-title d-block text-body-2 font-weight-medium grey--text text--darken-2 mb-1">
            Patient Profile Selection
          </label>
          <select-box
            :value="patientData.selectedDuplicatePatient ? patientData.selectedDuplicatePatient.id : null"
            :items="duplicatePatientsList"
            item-text="title"
            item-value="id"
            :clearable="false"
            single-line
            hint="Patient Name - Last 4 SSN"
            persistent-hint
            class="w-100"
            @change="updatedDuplicateField"
          ></select-box>
        </div>

        <div class="patient-details text-body-2">
          <div class="info-row">
            <span class="label">Name:</span>
            <span class="value">{{ patientData.selectedDuplicatePatient.name || 'N/A' }}</span>
          </div>
          <div class="info-row">
            <span class="label">DOB:</span>
            <span class="value">{{ patientData.selectedDuplicatePatient.date_of_birth || 'N/A' }}</span>
          </div>
          <div class="info-row">
            <span class="label">Gender:</span>
            <span class="value">{{ patientData.selectedDuplicatePatient.gender || 'N/A' }}</span>
          </div>
          <div class="info-row">
            <span class="label">Last 4 SSN:</span>
            <span class="value">{{ patientData.selectedDuplicatePatient.ssn_last_4 || '####' }}</span>
          </div>
          <div class="info-row">
            <span class="label">Facility:</span>
            <span class="value">{{ patientData.selectedDuplicatePatient.facility || 'N/A' }}</span>
          </div>
          <div class="info-row">
            <span class="label">Latest DOS:</span>
            <span class="value">{{ patientData.selectedDuplicatePatient.latest_visit_date || 'N/A' }}</span>
          </div>
        </div>
      </div>

      <!-- Right Column: Create New Patient -->
      <div class="col-12 col-sm-6 pl-sm-3 pr-0 py-0 d-flex flex-column">
        <div class="column-header pb-2 mb-3 border-bottom">
          <span class="font-weight-bold grey--text text--darken-3">Create New Patient</span>
        </div>

        <div class="patient-details text-body-2">
          <div class="info-row">
            <span class="label">Name:</span>
            <span class="value">{{ enteredPatientName }}</span>
          </div>
          <div class="info-row">
            <span class="label">DOB:</span>
            <span class="value">{{ formattedEnteredDob }}</span>
          </div>
          <div class="info-row">
            <span class="label">Gender:</span>
            <span class="value">{{ patientData.body.enteredPatient.gender || 'N/A' }}</span>
          </div>
          <div class="info-row">
            <span class="label">Last 4 SSN:</span>
            <span class="value">{{ patientData.body.enteredPatient.ssn_last_4 || patientData.body.enteredPatient.ssn || '####' }}</span>
          </div>
          <div class="info-row">
            <span class="label">Facility:</span>
            <span class="value">{{ patientData.body.enteredPatient.facility || patientData.body.enteredPatient.facility_name || 'N/A' }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mdiInformation } from '@mdi/js'

export default {
  props: {
    patientData: {
      type: Object,
      default: () => ({
        body: {
          enteredPatient: {
            first_name: '',
            middle_initial: '',
            last_name: '',
            suffix: '',
            dob: '',
            gender: '',
            ssn_last_4: '',
            facility: '',
          },
          duplicatePatients: [],
        },
        selectedDuplicatePatient: {},
      }),
    },
  },
  data() {
    return {
      duplicatePatients: [],
      icons: {
        mdiInformation,
      },
    }
  },
  computed: {
    duplicatePatientsList() {
      return this.patientData?.body?.duplicatePatients || []
    },
    enteredPatientName() {
      const p = this.patientData?.body?.enteredPatient || {}
      return `${p.first_name || ''} ${p.middle_initial || ''} ${p.last_name || ''} ${p.suffix || ''}`.trim() || 'N/A'
    },
    formattedEnteredDob() {
      const dob = this.patientData?.body?.enteredPatient?.dob
      if (!dob) return 'N/A'
      return this.$date ? this.$date(dob).format('MM/DD/YYYY') : dob
    },
  },
  watch: {
    duplicatePatientsList: {
      immediate: true,
      handler(newList) {
        if (newList && newList.length > 0) {
          if (!this.patientData.selectedDuplicatePatient || !this.patientData.selectedDuplicatePatient.id) {
            this.$set(this.patientData, 'selectedDuplicatePatient', { ...newList[0] })
            this.$emit('update:selectedDuplicatePatient', this.patientData.selectedDuplicatePatient)
          }
        }
      },
    },
  },
  created() {
    this.duplicatePatients = this.duplicatePatientsList
  },
  methods: {
    updatedDuplicateField(value) {
      const selectedId = typeof value === 'object' && value !== null ? (value.id || value.value) : value

      const matchedPatient = this.duplicatePatientsList.find(
        patient => String(patient.id) === String(selectedId)
      )

      if (matchedPatient) {
        this.$set(this.patientData, 'selectedDuplicatePatient', { ...matchedPatient })
        this.$emit('update:selectedDuplicatePatient', this.patientData.selectedDuplicatePatient)
      }
    },
  },
}
</script>

<style scoped>
.duplicate-patient-page-modal {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.duplicate-patient-page-modal .modal-title-text {
  margin-top: -16px !important;
  padding-top: 0 !important;
}

.duplicate-patient-page-modal .info-banner {
  position: relative;
  background-color: #e6f7ff;
  border: 1px solid #7ed1f7;
  border-radius: 8px !important;
  overflow: hidden;
}

.duplicate-patient-page-modal .info-banner .info-left-accent {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 8px;
  background-color: #7ed1f7;
}

.duplicate-patient-page-modal .info-text {
  color: #3caada;
}

.duplicate-patient-page-modal .instructions-list {
  list-style-type: disc;
}

.duplicate-patient-page-modal .count-badge {
  background-color: #e6f7ff;
  color: #0087cb;
  border-radius: 4px;
  padding: 2px 8px;
  font-weight: 700;
  font-size: 0.85rem;
}

.duplicate-patient-page-modal .border-bottom {
  border-bottom: 1px solid #e0e0e0;
}

/* Flexbox layout ensuring text wraps under value, NOT under label */
.duplicate-patient-page-modal .patient-details .info-row {
  margin-bottom: 8px;
  display: flex;
  align-items: flex-start;
}

.duplicate-patient-page-modal .patient-details .label {
  font-weight: 500;
  color: #616161;
  width: 100px;
  flex-shrink: 0;
}

.duplicate-patient-page-modal .patient-details .value {
  color: #212121;
  flex: 1;
  word-break: break-word;
  white-space: normal;
}

/* Hide clear "X" icon inside select box */
.select-duplicate ::v-deep .v-input__icon--clear {
  display: none !important;
}

/* Outer dialog container override */
::v-deep .duplicate-patient-page-dialog .v-card__title,
::v-deep .duplicate-patient-page-dialog > .v-card > .v-card__title {
  display: none !important;
  height: 0 !important;
  min-height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
}

::v-deep .duplicate-patient-page-dialog .v-card__actions {
  padding: 16px 24px 24px !important;
  display: grid !important;
  grid-template-columns: 1fr 1fr !important;
  gap: 16px !important;
  width: 100% !important;
}

::v-deep .duplicate-patient-page-dialog .v-card__actions .v-btn {
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;
  border-radius: 6px !important;
  height: 44px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  padding: 0 12px !important;
  margin: 0 !important;
}
</style>